// ── Concierge chat client (streaming + fallback) ────────────────────
//
// The Concierge is the client-facing persona (PRODUCT_VISION_agent_team.md):
// the warm front door that "talks like a person", not a console. This module
// is the wire to it — nothing about voice lives here, only transport.
//
// Backend (app/routers/concierge.py) exposes TWO endpoints:
//   POST /concierge/chat/stream  — SSE. Each event is `data: {"chunk": "..."}`
//        and the stream ends with `data: {"done": true, "full_text": "..."}`.
//        POST + SSE means fetch + ReadableStream, NOT EventSource (which is
//        GET-only and cannot carry an auth body).
//   POST /concierge/chat         — non-streaming; returns the whole answer.
//        Kept as the fallback so the user never sees a dead input.

import apiClient from './api'

const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

export interface ConciergeChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface ConciergeChatRequest {
  message: string
  // Omitted → backend defaults to the current authenticated principal.
  principal_id?: string
  conversation_history?: ConciergeChatMessage[]
}

export interface ConciergeStreamHandlers {
  // Called for each token/chunk as it arrives. `delta` is the new text, `full`
  // is everything received so far — the page renders `full` progressively.
  onChunk: (delta: string, full: string) => void
  onDone?: (fullText: string) => void
  signal?: AbortSignal
}

// Carries the partial text received before a stream broke, so the caller can
// keep what the analyst already said on screen rather than blanking it.
export class ConciergeStreamError extends Error {
  readonly partial: string
  constructor(message: string, partial: string) {
    super(message)
    this.name = 'ConciergeStreamError'
    this.partial = partial
  }
}

interface StreamPayload {
  chunk?: string
  done?: boolean
  full_text?: string
  // The backend has no explicit error event today (a failure aborts the
  // stream), but we honour one if it ever appears rather than rendering it
  // as text.
  error?: string
}

// Parse one SSE event block (already split on the blank-line separator) into
// its JSON `data:` payload. Returns null for comment/keep-alive lines.
function parseEventData(rawEvent: string): StreamPayload | null {
  const dataLines = rawEvent
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice('data:'.length).replace(/^ /, ''))
  if (dataLines.length === 0) return null

  const data = dataLines.join('\n')
  if (data === '[DONE]') return { done: true }
  try {
    return JSON.parse(data) as StreamPayload
  } catch {
    return null
  }
}

// Stream the concierge reply token-by-token over SSE.
//
// Resolves with the full text on clean completion. REJECTS on any transport
// failure so the caller can fall back:
//   - fetch/HTTP failure (unreachable, non-2xx, no body) → plain Error
//   - an SSE `error` event, or the stream ending without a `done` sentinel,
//     or an abort → ConciergeStreamError carrying whatever text arrived first.
// Text already delivered via onChunk is never swallowed.
export async function streamConciergeChat(
  request: ConciergeChatRequest,
  handlers: ConciergeStreamHandlers,
): Promise<string> {
  const token = localStorage.getItem('dw_token')

  const response = await fetch(`${API_BASE_URL}/api/v1/concierge/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(request),
    signal: handlers.signal,
  })

  if (!response.ok || !response.body) {
    throw new Error(`Concierge stream failed (${response.status})`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let full = ''
  let completed = false

  try {
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      // SSE events are separated by a blank line.
      let boundary: number
      while ((boundary = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, boundary)
        buffer = buffer.slice(boundary + 2)

        const payload = parseEventData(rawEvent)
        if (!payload) continue

        if (payload.error) {
          throw new ConciergeStreamError(payload.error, full)
        }
        if (typeof payload.chunk === 'string') {
          full += payload.chunk
          handlers.onChunk(payload.chunk, full)
        }
        if (payload.done) {
          completed = true
          if (typeof payload.full_text === 'string') full = payload.full_text
        }
      }
    }
  } catch (err) {
    // Abort or read error mid-stream — surface the partial we have so far.
    if (err instanceof ConciergeStreamError) throw err
    throw new ConciergeStreamError(
      err instanceof Error ? err.message : 'stream interrupted',
      full,
    )
  } finally {
    void reader.cancel().catch(() => {})
  }

  if (!completed) {
    throw new ConciergeStreamError('stream ended before completion', full)
  }

  handlers.onDone?.(full)
  return full
}

interface ConciergeChatResponse {
  content: string
  principal_id: string
  sources_queried: string[]
  cost_usd: number
  latency_ms: number
}

// Non-streaming fallback: one request, the whole answer. Routes through the
// shared axios client so the auth interceptor and 401 handling apply.
export async function sendConciergeChat(
  request: ConciergeChatRequest,
): Promise<string> {
  const response = await apiClient.post<ConciergeChatResponse>(
    '/concierge/chat',
    request,
  )
  return response.data.content
}
